import {
  Component,
  computed,
  input,
  InputSignalWithTransform,
  output, signal,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';

import {TreeNode} from 'primeng/api';
import {TreeSelectModule} from 'primeng/treeselect';
import {TreeNodeHelper} from './tree-node-helper';
import {Helpers} from '@selpia/shared/utils';
import {SvgSpriteIcon} from '../svg-sprite';

export type TreeData<T> = TreeNode<T> & {
  children?: Array<TreeData<T>>;
};

@Component({
  selector: 'lib-tree-dropdown',
  imports: [CommonModule, TreeSelectModule, FormsModule],
  templateUrl: './tree-dropdown.component.html',
  styleUrl: './tree-dropdown.component.scss',
})
export class TreeDropdownComponent<T, K extends keyof T> {
  readonly label = input<string | undefined>();
  readonly treeData = input.required<TreeData<T>>();
  readonly filter = input<boolean>(false);

  protected readonly innerId = signal(Helpers.generateRandomString())
    .asReadonly();

  selectionChange = output<T>();
  selection = input.required<T | undefined>();

  placeholder: InputSignalWithTransform<string, K> = input.required({
    alias: 'labelKey',
    transform: (v) => (this.treeData()?.data?.[v] as string) || '',
  });

  handleSelection(node: TreeNode<T> | null) {
    const data: T | undefined = node?.data || this.treeData().data;
    if (!data) {
      return;
    }
    this.selectionChange.emit(data);
  }

  protected readonly selectedNode = computed<TreeNode<T> | null>(() => {
    const selection = this.selection();

    if (!selection || selection === this.treeData().data) {
      return null;
    }
    return TreeNodeHelper.getMatchingNode<T>(selection, this.treeData());
  });
  protected readonly SvgSpriteIcon = SvgSpriteIcon;

  clearNode(event: MouseEvent) {
    event.stopPropagation();
    event.stopImmediatePropagation();
    const node = this.treeData().data;
    if (node) {
      this.selectionChange.emit(node);
    }
  }
}
